/**
 * Which photo a search by photo takes (owner, 2026-10-20): one at a time,
 * and only the formats the photo model reads. JPG, PNG and WEBP: neither
 * the browser resize nor the sidecar decodes HEIC, and an iPhone's picker
 * turns HEIC into JPEG when the input asks for these types.
 */
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const IMAGE_ACCEPT = IMAGE_TYPES.join(",");

export type PhotoNote = "one_at_a_time" | "format";

/** The first readable photo of a drop, a paste or a pick; `extra` when
 *  more files came with it; `unsupported` when files came but none in a
 *  format we read (a PDF or a video too: a drop that answers nothing
 *  reads as broken, owner 2026-10-21). */
export function pickPhoto(files: ArrayLike<{ type: string }> | null | undefined): {
  file: File | null;
  extra: boolean;
  unsupported: boolean;
} {
  const all = Array.from(files ?? []);
  const readable = all.filter((f) => IMAGE_TYPES.includes(f.type));
  return {
    file: (readable[0] as File | undefined) ?? null,
    extra: readable.length > 0 && all.length > 1,
    unsupported: all.length > 0 && readable.length === 0,
  };
}

/** What a file being dragged over the drop zone will be, from the item
 *  types the browser shows during the drag: "ok" when one is a photo we
 *  read, "bad" when every type is known and none is, "unknown" when the
 *  browser hides the types (Safari) or a link is dragged. Null when the
 *  drag carries no file or link at all (a text selection). */
export type DragVerdict = "ok" | "bad" | "unknown";
export function dragVerdict(
  dt: { types?: ArrayLike<string>; items?: ArrayLike<{ kind: string; type: string }> } | null | undefined,
): DragVerdict | null {
  const types = Array.from(dt?.types ?? []);
  if (!types.includes("Files")) return types.includes("text/uri-list") ? "unknown" : null;
  const files = Array.from(dt?.items ?? []).filter((it) => it.kind === "file");
  if (files.some((it) => IMAGE_TYPES.includes(it.type))) return "ok";
  if (files.length > 0 && files.every((it) => it.type)) return "bad";
  return "unknown";
}
